import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { cn, formatNumber } from "@/lib/utils";
import { LineGraph } from "@/components/ui/shared/graphs";
import { useEffect } from "react";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import { useStats } from "@/hooks/use-stats";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeftRight,
  BanknoteArrowDown,
  BanknoteArrowUp,
  ContactRoundIcon,
  Landmark,
  StoreIcon,
  Tags,
  TrendingUp,
  LayoutGrid,
  TrendingDown,
  Contact2,
  HandCoins,
  Truck,
} from "lucide-react";

import type { FinanceAccount } from "@/store/FinanceAccount";
import DataPlaceholder from "@/components/ui/shared/data-placeholder";
import StatisticsForm from "@/components/ui/shared/statistics-form";

export const Route = createFileRoute("/(dasboard)/finance/")({
  component: DashboardPage,
});

const menu = [
  { name: "Incomes", href: "/finance/incomes", icon: TrendingUp },
  { name: "Expenses", href: "/finance/expenses", icon: TrendingDown },

  { name: "Treasury account", href: "/finance/accounts", icon: Landmark },
  { name: "Transfers", href: "/finance/transfers", icon: ArrowLeftRight },
  { name: "Income categories", href: "/finance/income-categories", icon: Tags },
  {
    name: "Expense categories",
    href: "/finance/expense-categories",
    icon: LayoutGrid,
  },
  {
    name: "Customers debts",
    href: "/finance/debts/customers-debts",
    icon: Contact2,
  },
  {
    name: "Suppliers credits",
    href: "/finance/debts/suppliers-credits",
    icon: HandCoins,
  },
  {
    name: "Suppliers debts",
    href: "/finance/debts/suppliers-debts",
    icon: Truck,
  },
];

const template = [
  {
    label: "Total Incomes",
    key: "total_incomes",
    value: 0,
    formatAmount: true,
    color: "text-green-600",
    bg: "bg-green-100/70",
    icon: BanknoteArrowDown,
  },
  {
    label: "Total Expenses",
    key: "total_expenses",
    value: 0,
    formatAmount: true,
    color: "text-red-600",
    bg: "bg-red-100/70",
    icon: BanknoteArrowUp,
  },
  {
    label: "Customers debts",
    key: "customers_debts",
    value: 0,
    formatAmount: true,
    color: "text-gray-600",
    bg: "bg-gray-100/70",
    icon: ContactRoundIcon,
  },
  {
    label: "Suppliers debts",
    key: "suppliers_debts",
    value: 0,
    formatAmount: true,
    color: "text-gray-600",
    bg: "bg-gray-100/70",
    icon: StoreIcon,
  },
  {
    label: "Suppliers credits",
    key: "suppliers_credits",
    value: 0,
    formatAmount: true,
    color: "text-red-600",
    bg: "bg-red-100/70",
    icon: StoreIcon,
  },
];

function DashboardPage() {
  const store = useAppStore();

  const { form, stats, setOverview } = useStats({
    template,
  });

  const { t } = useTranslate();

  const balance = store.financeTransaction.stats.accounts.reduce(
    (sum: number, item: FinanceAccount) => sum + item.balance,
    0
  );
  const totalDebt = store.financeDebt.stats.total_debt;

  const fetchStats = () => {
    store.financeTransaction
      .getStats(form.values.start_date, form.values.end_date)
      .then((data) => {
        setOverview(data, ["total_incomes", "total_expenses"]);
      });

    store.financeDebt
      .getStats(form.values.start_date, form.values.end_date)
      .then((data) => {
        setOverview(data, [
          "customers_debts",
          "suppliers_debts",
          "suppliers_credits",
        ]);
      });

    store.financeAccount.fetch({});
  };

  useEffect(() => {
    if (store.auth.session) {
      fetchStats();
    }
  }, [store.auth.session]);

  return (
    <div className="mx-auto">
      <div className="flex items-center  mb-6 gap-2 flex-wrap border-b pb-4 border-gray-300">
        {menu.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className="flex items-center text-gray-800 gap-3 cursor-pointer hover:bg-secondary-50 transition-all duration-300 border border-gray-200 bg-white  py-2 rounded-full px-4"
          >
            <item.icon className="text-secondary-800" />
            <span className="text-sm"> {t(item.name)}</span>
          </Link>
        ))}
      </div>

      <StatisticsForm form={form} fetchStats={fetchStats} />

      <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-5">
        {stats.map((stat: any, index: number) => (
          <Card
            key={index}
            className={cn("rounded-2xl bg-white transition hover:shadow")}
          >
            <CardContent className="flex gap-4 p-4">
              <div>
                <div className={cn("rounded-md bg-white p-2", stat.bg)}>
                  <stat.icon className={cn("h-6 w-6", stat.color)} />
                </div>
              </div>
              <div>
                <p className="text-sm">{t(stat.label)}</p>
                <div className="flex gap-1 font-semibold">
                  <span>
                    {stat.formatAmount
                      ? store.shop.formatAmount(stat.value)
                      : typeof stat.value == "number"
                        ? formatNumber(stat.value)
                        : stat.value}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <section className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-8 w-full">
        <div className="col-span-3 space-y-6">
          <article className="col-span-2">
            <LineGraph
              title={t("Incomes and expenses")}
              colors={store.financeTransaction.stats.series.datasets.map(
                (item: any) => item.color
              )}
              categories={store.financeTransaction.stats.series.labels}
              series={store.financeTransaction.stats.series.datasets.map(
                (item: any) => ({
                  name: t(item.name),
                  data: item.data,
                })
              )}
            />
          </article>
          <article className="col-span-2">
            <LineGraph
              title={t("Debts")}
              colors={store.financeDebt.stats.series.datasets.map(
                (item: any) => item.color
              )}
              categories={store.financeDebt.stats.series.labels}
              series={store.financeDebt.stats.series.datasets.map(
                (item: any) => ({
                  name: t(item.name),
                  data: item.data,
                })
              )}
            />
          </article>
          <article className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 p-6 bg-white rounded-md">
              <h2 className="font-semibold"> {t("Top Incomes categories")}</h2>
              {store.financeTransaction.stats.top_income_categories.length !=
              0 ? (
                <ul className="mt-2">
                  {store.financeTransaction.stats.top_income_categories.map(
                    (item: any, index: number) => (
                      <li
                        key={`top_income_categories${index}`}
                        className="md:flex md:justify-between py-2 border-b border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                            {index + 1}
                          </div>
                          <p>{item.title}</p>
                        </div>
                        <p className="font-semibold">
                          {store.shop.formatAmount(item.total)}
                        </p>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <DataPlaceholder />
              )}
            </div>
            <div className="border border-gray-200 p-6 bg-white rounded-md">
              <h2 className="font-semibold"> {t("Top Expenses categories")}</h2>
              {store.financeTransaction.stats.top_expense_categories.length !=
              0 ? (
                <ul className="mt-2">
                  {store.financeTransaction.stats.top_expense_categories.map(
                    (item: any, index: number) => (
                      <li
                        key={`top_expense_categories${index}`}
                        className="md:flex md:justify-between py-2 border-b border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                            {index + 1}
                          </div>
                          <p>{item.title}</p>
                        </div>
                        <p className="font-semibold">
                          {store.shop.formatAmount(item.total)}
                        </p>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <DataPlaceholder />
              )}
            </div>
          </article>
          <article className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-gray-200 p-6 bg-white rounded-md">
              <h2 className="font-semibold"> {t("Top Customers Debitors")}</h2>
              {store.financeDebt.stats.top_customers_debitors.length != 0 ? (
                <ul className="mt-2">
                  {store.financeDebt.stats.top_customers_debitors.map(
                    (item: any, index: number) => (
                      <li
                        key={`top_customers_debitors${index}`}
                        className="py-2 flex gap-2 border-b border-gray-200"
                      >
                        <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p>{`${item.payer.first_name} ${item.payer.last_name}`}</p>
                          <p className="font-semibold text-sm">
                            {store.shop.formatAmount(item.amount)}
                          </p>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <DataPlaceholder />
              )}
            </div>
            <div className="border border-gray-200 p-6 bg-white rounded-md">
              <h2 className="font-semibold"> {t("Top Suppliers Debitors")}</h2>
              {store.financeDebt.stats.top_suppliers_debitors.length != 0 ? (
                <ul className="mt-2">
                  {store.financeDebt.stats.top_suppliers_debitors.map(
                    (item: any, index: number) => (
                      <li
                        key={`top_suppliers_debitors${index}`}
                        className="py-2 flex gap-2 border-b border-gray-200"
                      >
                        <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p>{item.payer.name}</p>
                          <p className="font-semibold text-sm">
                            {store.shop.formatAmount(item.amount)}
                          </p>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <DataPlaceholder />
              )}
            </div>
            <div className="border border-gray-200 p-6 bg-white rounded-md">
              <h2 className="font-semibold"> {t("Top Suppliers Creditors")}</h2>
              {store.financeDebt.stats.top_suppliers_creditors.length != 0 ? (
                <ul className="mt-2">
                  {store.financeDebt.stats.top_suppliers_creditors.map(
                    (item: any, index: number) => (
                      <li
                        key={`top_suppliers_creditors${index}`}
                        className="py-2 flex gap-2 border-b border-gray-200"
                      >
                        <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p>{item.receiver.name}</p>
                          <p className="font-semibold">
                            {store.shop.formatAmount(item.amount)}
                          </p>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <DataPlaceholder />
              )}
            </div>
          </article>
        </div>

        <aside className="space-y-6">
          <div className="bg-white rounded-md border border-gray-200 p-4">
            <div
              className={cn("rounded-md p-4", {
                "bg-red-50": balance < 0,
                "bg-green-50": balance > 0,
              })}
            >
              <p className="font-semibold">{t("Total Balance")}</p>
              <p
                className={cn("text-2xl font-semibold", {
                  "text-red-700": balance < 0,
                  "text-green-700": balance > 0,
                })}
              >
                {store.shop.formatAmount(balance)}
              </p>
            </div>
            <div
              className={cn("mt-4 rounded-md p-4", {
                "bg-red-50": totalDebt < 0,
                "bg-green-50": totalDebt > 0,
              })}
            >
              <p className="font-semibold">{t("Gross Debt")}</p>
              <p
                className={cn("text-2xl font-semibold", {
                  "text-red-700": totalDebt < 0,
                  "text-green-700": totalDebt > 0,
                })}
              >
                {store.shop.formatAmount(totalDebt)}
              </p>
            </div>
            <ul className="space-y-4 mt-4">
              {store.financeTransaction.stats.accounts.map((item: any) => (
                <li
                  key={`financeAccount${item.id}`}
                  className={cn("px-4 py-2 rounded-md bg-gray-50")}
                >
                  <div className="font-semibold">{item.name}</div>
                  <div
                    className={cn("font-semibold", {
                      "text-red-800": item.balance < 0,
                      "text-green-800": item.balance > 0,
                    })}
                  >
                    {store.shop.formatAmount(item.balance)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
